/**
 * What an event stores when a validation agent is bound to it — SIMS-8223.
 *
 * 🔴 Declared HERE, not beside the catalog types in validationAgent.interface.ts,
 * and not for style. That file imports `ValidationType` from this one; if this
 * file imported the binding back, the two would form a CYCLE — and `ValidationType`
 * is an ENUM, a real runtime value, so under a cycle whichever module loads
 * second can see it as `undefined`. The identical mistake was made and measured
 * on the backend (Event.ts / ValidationAgent.ts), where it silently stripped a
 * Mongoose enum constraint while tsc stayed clean.
 *
 * Dependency runs one way: validationAgent.interface -> event.interface.
 */
export interface EventValidationAgentBinding {
	agentKey: string;
	/** AC7 — the version bound to, never "whatever is current". */
	version: string;
	config?: Record<string, unknown>;
}

export enum ValidationType {
	TEST_RESULT_MANUAL_INPUT = "TEST_RESULT_MANUAL_INPUT",
	GENERIC_MANUAL_INPUT = "GENERIC_MANUAL_INPUT",
	PHOTOS = "PHOTOS",
	FORM = "FORM",
	ATTENDANCE = "ATTENDANCE",
	COMPOUND = "COMPOUND",
	WATCH_VIDEO = "WATCH_VIDEO",
	CAPTURE_VIDEO = "CAPTURE_VIDEO",
}

export enum CompletionRequirement {
	ALL_REQUIRED = "ALL_REQUIRED",
	ALL_STEPS = "ALL_STEPS",
	REQUIRED_ONLY = "REQUIRED_ONLY",
}

export interface EventResult {
	_id?: string;
	id: string;
	display: string;
	tamperedDisplayText?: string;
	shortName?: string;
	displayColor?: string;
	allowForEntrySelection: boolean;
	allowForEditionWithOutPerm: boolean;
	validResult: boolean;
	disenrollmentJobStatus?: boolean;
	requireConfirmation?: boolean;
	missEntry?: boolean;
	showAnalytes?: boolean;
}

export interface EventKPI {
	name: string;
	url: string;
	active?: boolean;
	position?: number;
	variables?: Array<{ name: string; value: string }>;
}

export interface SectionConfig {
	action: string;
	position: number;
	title: string;
	customStructure?: Record<string, unknown>;
}

export interface PhotoConfig {
	minPhotos: number;
	maxPhotos: number;
	sequentialCapture?: boolean;
	allowRetake?: boolean;
}

export interface FormConfig {
	surveySchema: Record<string, unknown>;
	surveySchemaVersion: string;
	uploadedAt?: string;
	uploadedBy?: string;
}

export interface CompoundStep {
	order: number;
	eventId: string;
	required?: boolean;
}

export interface CompoundConfig {
	steps: CompoundStep[];
	allowPartialSubmission?: boolean;
	completionRequirement?: CompletionRequirement;
}

export interface DefaultSubmissionWindow {
	duration: number;
	unit: string;
	reason?: string;
}

export interface NotificationMetadata {
	notificationTitle?: string;
	notificationMessage?: string;
	reminderTitle?: string;
	reminderMessage?: string;
	activityDescription?: string;
	timingGuidance?: string;
}

export interface MobileCard {
	title?: string;
	body?: string;
	imageUrl?: string;
}

export interface VideoConfig {
	// Watch_Video fields
	videoUrl?: string;
	videoId?: string;
	duration?: number;
	title?: string;
	description?: string;
	thumbnailUrl?: string;
	minWatchPercentage?: number;
	allowSkip?: boolean;
	// Capture_Video fields
	maxDuration?: number;
	minDuration?: number;
	allowRetake?: boolean;
	uploadFormat?: string;
}

export interface Event {
	_id: string;
	name: string;
	verificationType: "Manual" | "Automatic";
	validationType: ValidationType;
	requireMobileValidation?: boolean;
	cupsAndAnalytes?: boolean;
	eventResults?: EventResult[];
	dashboardKPIs?: EventKPI[];
	profileStatisticsKPIs?: EventKPI[];
	sectionConfig?: SectionConfig;
	photoConfig?: PhotoConfig;
	formConfig?: FormConfig;
	compoundConfig?: CompoundConfig;
	videoConfig?: VideoConfig;

	/**
	 * SIMS-8223 — the validation agent bound to this event.
	 *
	 * Optional, and absent on every event that predates the feature: an absence
	 * reads as "not agent-validated, follows the manual review path" (AC9), never
	 * as a decision.
	 */
	validationAgent?: EventValidationAgentBinding;
	isSubEvent?: boolean;
	defaultSubmissionWindow?: DefaultSubmissionWindow;
	accountId?: string | null;
	notificationMetadata?: NotificationMetadata;
	mobileCard?: MobileCard;
	createdAt: string;
	updatedAt: string;
}

export interface EventRequest {
	name: string;
	verificationType: "Manual" | "Automatic";
	validationType: ValidationType;
	requireMobileValidation?: boolean;
	cupsAndAnalytes?: boolean;
	eventResults?: EventResult[];
	dashboardKPIs?: EventKPI[];
	profileStatisticsKPIs?: EventKPI[];
	sectionConfig?: SectionConfig;
	photoConfig?: PhotoConfig;
	formConfig?: FormConfig;
	compoundConfig?: CompoundConfig;
	videoConfig?: VideoConfig;

	/** SIMS-8223 — see the same field on `Event` above. */
	validationAgent?: EventValidationAgentBinding;
	isSubEvent?: boolean;
	defaultSubmissionWindow?: DefaultSubmissionWindow;
	accountId?: string | null;
	notificationMetadata?: NotificationMetadata;
	mobileCard?: MobileCard;
	createdBy?: string;
	updatedBy?: string;
}
